import { useCallback } from 'react';
import { GetMarkRange } from '@remirror/core';
import { useCommands, useRemirrorContext } from '@remirror/react';

import { WordWithRange } from '@replacements/hooks/floatingReplacementsToolbar/useWordSelection';
import { ReplacementItem } from '@replacements/components/FloatingReplacementsToolbar/SingleReplacementItem';
import { ReplacementSourceEnum } from '@replacements/utils/floatingReplacementsToolbarUtils';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  EnhancedPhoneticReplacement,
  setReplacementsState,
  Suggestion,
} from '../../../../store/ReplacementsSlice';
import { countActiveReplacements } from '../../../EditorV2/utils';
import {
  EventName,
  useEventsContext,
} from '../../../../context/events/EventsContext';

export const getStorageKey = (projectId: string) =>
  `${projectId}:appliedOxfordSuggestions`;

export const getStoredSuggestionRanges = (projectId: string) => {
  const STORAGE_KEY = getStorageKey(projectId);
  const raw = sessionStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : [];
};

export interface AppliedSuggestion {
  text: string;
  replacement: string;
  from: number;
  to: number;
  isRespelling: boolean;
}

interface useDropdownSelectionChangeProps {
  replacementsLibrary: EnhancedPhoneticReplacement[];
  effectiveText: string;
  selectedValue: string;
  markWithRange?: GetMarkRange | null;
  wordWithRange?: WordWithRange | null;
  projectId: string;
}
/*
 * Returns event handler callbacks for user selection changes in the
 * floating toolbar replacements dropdown menu
 */
const useDropdownSelectionChange = (props: useDropdownSelectionChangeProps) => {
  const {
    replacementsLibrary,
    effectiveText,
    selectedValue,
    markWithRange,
    wordWithRange,
    projectId,
  } = props;
  const [eventTracking] = useEventsContext();
  const appDispatch = useAppDispatch();
  const { replacedWordsCount, oxfordSuggestions, llmSuggestions } =
    useAppSelector(appState => appState.replacements);
  const { updateReplacementForMark } = useCommands();
  const { view } = useRemirrorContext({ autoUpdate: true });
  const STORAGE_KEY = getStorageKey(projectId);

  // Add or update an applied word
  const storeAppliedSuggestion = useCallback(
    (suggestion: AppliedSuggestion) => {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const applied = raw ? JSON.parse(raw) : [];

      applied.push(suggestion);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(applied));
    },
    [STORAGE_KEY]
  );

  /**
   * on change callback for when a library replacement or none is selected in dropdown
   */
  const handleReplacementChange = useCallback(
    (item: ReplacementItem) => {
      const replacement = item.value.split(':').pop() ?? '';
      const { source, isRespelling } = item;
      const noneSelected = replacement === '';
      const previousReplacement = selectedValue.split(':').pop();

      const newReplacement = replacementsLibrary.find(
        ({ text, replacement: defaultReplacement, enabled }) =>
          enabled &&
          text === effectiveText &&
          defaultReplacement === replacement
      );

      const updateReplacementCount = () => {
        let newReplacementCount = replacedWordsCount;

        if (newReplacement && previousReplacement === '') {
          newReplacementCount = replacedWordsCount + 1;
        } else if (!newReplacement) {
          newReplacementCount = Math.max(replacedWordsCount - 1, 0);
        }

        if (newReplacementCount !== replacedWordsCount) {
          appDispatch(
            setReplacementsState({ replacedWordsCount: newReplacementCount })
          );
        }
      };

      // Track the change
      eventTracking.track({
        type: EventName.FloatingPhoneticReplacementModified,
        data: {
          text: effectiveText,
          previousReplacement: previousReplacement || '',
          newReplacement: replacement,
        },
      });

      if (markWithRange && noneSelected) {
        updateReplacementForMark(markWithRange, {
          newReplacement: previousReplacement,
          enabled: false,
          replacementChanged: true,
          source,
        });
      } else if (markWithRange && !noneSelected) {
        updateReplacementForMark(markWithRange, {
          newReplacement: replacement,
          originalText: effectiveText,
          enabled: true,
          replacementChanged: true,
          isRespelling: newReplacement?.isRespelling ?? false,
          source,
          entryId: newReplacement?.id ?? '',
        });
      } else if (wordWithRange && !noneSelected) {
        const schema = view?.state?.schema;
        const markType = schema?.marks?.phonetic;
        if (!markType) return;

        const realMark = markType.create({
          replacement,
          originalText: effectiveText,
          enabled: true,
          replacementChanged: false,
          isRespelling,
          source,
          entryId: newReplacement?.id ?? '',
        });

        const oxfordMark: GetMarkRange = {
          ...wordWithRange,
          mark: realMark,
        };

        updateReplacementForMark(oxfordMark, {
          newReplacement: replacement,
          originalText: effectiveText,
          enabled: true,
          replacementChanged: false,
          isRespelling: true,
          source,
          entryId: newReplacement?.id ?? '',
        });
      }

      // Count update
      updateReplacementCount();
    },
    [
      appDispatch,
      effectiveText,
      eventTracking,
      markWithRange,
      replacedWordsCount,
      replacementsLibrary,
      selectedValue,
      updateReplacementForMark,
      view?.state?.schema,
      wordWithRange,
    ]
  );

  /**
   * on change callback for when an oxford/llm suggestion is selected in dropdown
   */
  const handleSuggestionChange = useCallback(
    (
      suggestion: {
        text: string;
        replacement: string;
        source: ReplacementSourceEnum;
      },
      mark: WordWithRange
    ) => {
      const { text, replacement, source } = suggestion;

      const newEntry: Suggestion = {
        text,
        replacement,
        isRespelling: true,
        source,
      };

      eventTracking.track({
        type: EventName.FloatingPhoneticReplacementModified,
        data: {
          text: markWithRange?.text ?? wordWithRange?.text ?? '',
          previousReplacement: selectedValue,
          newReplacement: replacement,
        },
      });

      if (newEntry.source === ReplacementSourceEnum.LlmSuggestion) {
        eventTracking.track({
          type: EventName.SmartSuggestionSelected,
          data: {
            text: markWithRange?.text ?? wordWithRange?.text ?? '',
            replacement,
          },
        });
        appDispatch(
          setReplacementsState({
            llmSuggestions: [...llmSuggestions, newEntry],
          })
        );
      } else {
        eventTracking.track({
          type: EventName.FloatingPhoneticReplacementModified,
          data: {
            text: markWithRange?.text ?? wordWithRange?.text ?? '',
            previousReplacement: selectedValue,
            newReplacement: replacement,
          },
        });

        appDispatch(
          setReplacementsState({
            oxfordSuggestions: [...oxfordSuggestions, newEntry],
          })
        );
      }

      // Apply mark as before
      if (mark) {
        const { schema } = view.state;
        const markType = schema.marks.phonetic;
        const { from, to } = view.state.selection;

        const realMark = markType.create({
          replacement,
          enabled: true,
          replacementChanged: true,
          isRespelling: true,
          source,
        });

        const suggestionMark = {
          from,
          to,
          text: view.state.doc.textBetween(from, to),
          mark: realMark,
        };

        storeAppliedSuggestion({ ...newEntry, to, from });

        updateReplacementForMark(suggestionMark, {
          newReplacement: replacement,
          originalText: effectiveText,
          enabled: true,
          replacementChanged: true,
          isRespelling: true,
          source,
        });

        const updatedState = view.state;
        const newCount = countActiveReplacements(updatedState);
        appDispatch(setReplacementsState({ replacedWordsCount: newCount }));
      }
    },
    [
      eventTracking,
      markWithRange?.text,
      wordWithRange?.text,
      selectedValue,
      appDispatch,
      llmSuggestions,
      oxfordSuggestions,
      view.state,
      storeAppliedSuggestion,
      updateReplacementForMark,
      effectiveText,
    ]
  );

  return {
    onReplacementChange: handleReplacementChange,
    onSuggestionChange: handleSuggestionChange,
  };
};

export default useDropdownSelectionChange;
